import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { merchantAuthController } from '../controllers/authController';

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  step: string | null;
  login: (email: string, password: string) => Promise<any>;
  logout: () => void;
  loading: boolean;
  checkAuthStatus: () => Promise<boolean>;
  setStep: (step: string | null | undefined) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!token);
  const [step, setStep] = useState<string | null>(localStorage.getItem('step'));
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  // Function to check authentication status - memoized to prevent infinite re-renders
  const checkAuthStatus = useCallback(async (): Promise<boolean> => {
    const currentToken = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    if (!currentToken || !refreshToken) {
      setIsAuthenticated(false);
      return false;
    }

    try {
      // Try to reissue token with refresh token
      const response = await merchantAuthController.reissueJwtToken({ refreshToken });
      const newToken = response.data.data.token;

      localStorage.setItem('token', newToken);
      setToken(newToken);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      console.error('Token verification failed:', error);
      // Clear tokens if invalid
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('step');
      setToken(null);
      setStep(null);
      setIsAuthenticated(false);
      return false;
    }
  }, []);

  useEffect(() => {
    // Check if token exists and is valid when component mounts
    const verifyToken = async () => {
      await checkAuthStatus();
      setLoading(false);
    };

    verifyToken();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await merchantAuthController.login({ email, password });
      const { token: newToken, refreshToken, step: newStep } = response.data.data;

      localStorage.setItem('token', newToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('step', newStep);

      setToken(newToken);
      setStep(newStep);
      setIsAuthenticated(true);

      return { success: true, step: newStep };
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, error };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('step');
    setToken(null);
    setStep(null);
    setIsAuthenticated(false);
    navigate('/login');
  };

  const value = {
    isAuthenticated,
    token,
    step,
    login,
    logout,
    loading,
    checkAuthStatus,
    setStep
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 